import { useState, useEffect, CSSProperties } from "react"
import { useQuery } from "react-query"
import Select from "react-select"
import Async, { useAsync } from "react-select/async"
import CreatableSelect from "react-select/creatable"
import axios from "axios"
import cn from "classnames"
import customStyles from "./styles"
import DropdownIndicatorForSelect from "components/DropdownIndicatorForSelect"
import ConditionalView from "../ConditionalView"

const fetchDetails =
  (
    endpoint: string,
    labelKey?: string,
    getLabel?: Function,
    next_endpoint?: string,
    pageSize?: string
  ) =>
  async () => {
    const finalPageSize = pageSize ?? 9000
    const joiner = endpoint.includes("?") ? "&" : "?"
    const { data } = await axios.get<any>(
      `${endpoint}${joiner}page_size=${finalPageSize}`
    )
    const output = {} as {
      [key: number | string]: {
        value: number | string
        label: string
        data: any
      }
    }
    const mapData = data.results ?? data
    mapData.forEach((item: any) => {
      output[`a_${item.id}`] = {
        value: item.id,
        label: getLabel ? getLabel(item) : item[labelKey ?? ""],
        data: item,
      }
    })

    if (!next_endpoint) return output

    const next_joiner = next_endpoint.includes("?") ? "&" : "?"
    const nextEndpoint = `${next_endpoint}${next_joiner}${
      next_endpoint?.includes("page_size") ? "" : "page_size=10000"
    }`

    const { data: newData } = await axios.get<any>(nextEndpoint)

    const newMapData = newData.results ?? newData
    newMapData.forEach((item: any) => {
      output[`b_${item.id}`] = {
        value: item.id,
        label: getLabel ? getLabel(item) : item[labelKey ?? ""],
        data: item,
      }
    })

    return output
  }

const SinglePicker = (props: {
  endpoint: string
  labelKey?: string
  placeholder?: string
  loadOptionsFunction?: any
  async?: boolean
  defaultSelection?: any
  selected?: any
  className?: string
  label?: string
  flush?: boolean
  canCreate?: boolean
  selectFirst?: boolean
  nextEndpoint?: string
  labelClass?: string
  isDisabled?: boolean
  isClearable?: boolean
  pageSize?: any
  getLabel?: Function
  errorText?: string
  menuPlacement?: "auto" | "bottom" | "top"
  styles?: any
  onSelect?: (item: any) => void
}) => {
  const { data, isLoading } = useQuery(
    ["pickers", props.endpoint],
    fetchDetails(
      props.endpoint,
      props.labelKey,
      props.getLabel,
      props.nextEndpoint,
      props.pageSize
    ),
    {
      cacheTime: 100000,
      refetchOnWindowFocus: false,
      enabled: !props.async,
    }
  )
  const [show, setShow] = useState(false)

  const style: CSSProperties = props.flush ? { marginRight: 0 } : {}
  style.border = props.errorText ? "1px solid #FF0000" : ""

  const SelectComponent = props.canCreate ? CreatableSelect : Select
  const selected = props.selected ?? ""
  let key = selected
    ? selected?.startsWith?.("a") || selected?.startsWith?.("b")
      ? props?.selected
      : `a_${props.selected}`
    : ""

  useEffect(() => {
    setTimeout(() => {
      setShow(true)
    }, 1)
  }, [])

  useEffect(() => {
    setShow(false)
    setTimeout(() => {
      setShow(true)
    }, 1)
  }, [JSON.stringify(props.defaultSelection)])

  useEffect(() => {
    if (!props.selectFirst || isLoading) return

    const new_key = key || Object.keys(data ?? {})[0]
    if (data?.[new_key]) {
      props?.onSelect?.(data?.[new_key])
    }
  }, [props.selectFirst, isLoading])

  useEffect(() => {
    if (props.async) return
    setShow(false)
    setTimeout(() => {
      setShow(true)
    }, 10)
  }, [props.selected])

  if (!show) return <div className={props.className} />

  if (isLoading)
    return (
      <SelectComponent
        placeholder={"Loading..."}
        options={[]}
        isClearable={props.isClearable}
        onChange={props.onSelect}
        styles={customStyles(style)}
        defaultValue={data?.[key]}
      />
    )
  if (props.selectFirst) {
    key = key || Object.keys(data ?? {})[0]
  }

  // if (!props.selected) return null
  return (
    <div className={cn("flex-c-box", props.className)}>
      {!props.label ? null : (
        <>
          <span
            className={cn("mb-1 font-bold text-primary-text", props.labelClass)}
          >
            {props.label}
          </span>
        </>
      )}
      <ConditionalView condition={!!props.async}>
        <Async
          isDisabled={props.isDisabled}
          cacheOptions
          defaultOptions
          isClearable={props.isClearable}
          onChange={props.onSelect}
          placeholder={props.placeholder ?? "Choose..."}
          loadOptions={props.loadOptionsFunction}
          styles={props?.styles ? props?.styles : customStyles(style)}
          defaultValue={props.defaultSelection}
          components={
            props.errorText
              ? { DropdownIndicator: DropdownIndicatorForSelect }
              : {}
          }
        />
        <SelectComponent
          isDisabled={props.isDisabled}
          menuPlacement={props.menuPlacement ?? "auto"}
          placeholder={props.placeholder ?? "Choose..."}
          options={Object.values((data ?? {}) as any)}
          isClearable={props.isClearable}
          onChange={props.onSelect}
          styles={props?.styles ? props?.styles : customStyles(style)}
          defaultValue={data?.[key]}
          components={
            props.errorText
              ? { DropdownIndicator: DropdownIndicatorForSelect }
              : {}
          }
        />
      </ConditionalView>
    </div>
  )
}

export default SinglePicker
